import React from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import BrandLogo from './BrandLogo';
import { DomainKey } from '../../config/domains';
import ReusableButton from './ReusableButton';
import Table from './Table';

export interface BasePrecheckItem {
  id: number | string;
  company: string;
  contact: string;
  email?: string;
  sourceDomain: DomainKey;
  createdAt: string;
  channel?: 'web' | 'api';
}

interface PrecheckTableProps<T extends BasePrecheckItem> {
  data: T[];
  type: 'ECOM' | 'POS' | 'COMBINED';
  renderMiddleColumns?: (item: T) => React.ReactNode;
  middleHeaders?: string[];
}

function PrecheckTable<T extends BasePrecheckItem>({
  data,
  type,
  renderMiddleColumns,
  middleHeaders = [],
}: PrecheckTableProps<T>) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleView = (id: number | string, itemType?: string) => {
    // For combined type, extract the actual id and type
    if (type === 'COMBINED' && typeof id === 'string') {
      const [typePrefix, actualId] = id.split('-');
      const route = typePrefix === 'POS' ? '/precheck-pos' : '/precheck';
      navigate(`${route}/${actualId}`);
    } else if (type === 'POS') {
      navigate(`/precheck-pos/${id}`);
    } else {
      navigate(`/precheck/${id}`);
    }
  };

  const renderRow = (item: T) => {
    return (
      <>
        {/* Company */}
        <td className="border px-2 py-2 capitalize break-words">
          {item.company.toLowerCase()}
          {item.channel === 'api' && (
            <span
              title={t('apiKeys.submittedViaApi')}
              className="ml-2 inline-flex items-center rounded-md bg-indigo-50 px-1.5 py-0.5 text-xs font-medium normal-case text-indigo-700 ring-1 ring-inset ring-indigo-600/20"
            >
              API
            </span>
          )}
        </td>

        {/* Contact */}
        <td className="border px-2 py-2 break-words">
          <span className="capitalize">{item.contact.toLowerCase()}</span>
          {item.email && (
            <>
              <br />
              <a
                href={`mailto:${item.email}`}
                className="text-blue-600 visited:text-purple-600 underline underline-offset-2 hover:text-blue-400 break-all"
              >
                <span className="lowercase">{item.email}</span>
              </a>
            </>
          )}
        </td>

        {/* Middle columns (dynamic) */}
        {renderMiddleColumns && renderMiddleColumns(item)}

        {/* Domain */}
        <td className="border px-2 py-2 text-center w-24">
          <div className="flex justify-center">
            <BrandLogo source={item.sourceDomain} variant="dark" className="h-8 w-auto" />
          </div>
        </td>

        {/* Submitted Date */}
        <td className="border px-2 py-2 whitespace-nowrap w-28">
          {format(new Date(item.createdAt), 'MMM d, yyyy')}
        </td>

        {/* Actions */}
        <td className="border px-2 py-2 w-24">
          <ReusableButton
            onClick={() => handleView(item.id)}
            icon={<BookOpenIcon className="size-5" aria-hidden="true" />}
            label={t('common.view')}
            className="my-1"
          />
        </td>
      </>
    );
  };

  const headers = [
    t('merchant.company'),
    t('merchant.contact'),
    ...middleHeaders,
    t('common.domain'),
    t('common.submitted'),
    t('common.actions'),
  ];

  return (
    <Table
      headers={headers}
      data={data}
      renderRow={renderRow}
      keyExtractor={(item) => item.id.toString()}
    />
  );
}

export default PrecheckTable;
